import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Mail, Calendar, FileText, CheckSquare, Zap, MoreHorizontal } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const FloatingIcon = ({ Icon, delay, color, size = 60 }) => {
  const randomX = Math.random() * 200 - 100;
  const randomY = Math.random() * 100 - 50;

  return (
    <div
      className="absolute animate-float"
      style={{
        animationDelay: `${delay}s`,
        animationDuration: '6s',
        '--float-x': `${randomX}px`,
        '--float-y': `${randomY}px`
      }}
    >
      <div
        className="rounded-full flex items-center justify-center shadow-2xl backdrop-blur-sm"
        style={{
          backgroundColor: color,
          width: `${size}px`,
          height: `${size}px`
        }}
      >
        <Icon className="text-white" size={size * 0.5} />
      </div>
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa tu correo y contraseña.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) throw error;

      toast({
        title: 'Bienvenido a Ventes Suite',
        description: 'Sesión iniciada correctamente.'
      });

      navigate('/dashboard');

    } catch (error) {
      console.error("Error de Login:", error.message);
      toast({
        title: 'Acceso Denegado',
        description: 'Correo o contraseña incorrectos.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fafafa] via-[#f0f4ff] to-[#fdf4ff] dark:bg-none dark:bg-[#0d0d0f] flex items-center justify-center overflow-hidden relative transition-colors duration-300">
      {/* Floating Icons Animation */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative w-96 h-96">
          <FloatingIcon Icon={Mail}          delay={0}   color="#6366f1" size={70} />
          <FloatingIcon Icon={Calendar}      delay={0.5} color="#10b981" size={65} />
          <FloatingIcon Icon={FileText}      delay={1}   color="#f59e0b" size={60} />
          <FloatingIcon Icon={CheckSquare}   delay={1.5} color="#a855f7" size={65} />
          <FloatingIcon Icon={Zap}           delay={2}   color="#ec4899" size={70} />
          <FloatingIcon Icon={MoreHorizontal} delay={2.5} color="#06b6d4" size={55} />
        </div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-white/80 backdrop-blur-xl dark:bg-[#111114] rounded-3xl shadow-[0_4px_40px_rgba(99,102,241,0.12)] dark:shadow-none p-8 border border-white/90 dark:border-[#1e1e24] transition-colors duration-300">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold text-[#1a1625] dark:text-[#e5e5e5] mb-2 tracking-tight">Ventes Suite</h1>
            <p className="text-[#6b7280] dark:text-[#888] text-lg">Tu centro de operaciones</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Input
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 text-base bg-white dark:bg-[#1a1a20] border-indigo-100 dark:border-[#1e1e24] focus:border-indigo-400 dark:focus:border-violet-500 text-[#1a1625] dark:text-[#e5e5e5] transition-colors"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 text-base bg-white dark:bg-[#1a1a20] border-indigo-100 dark:border-[#1e1e24] focus:border-indigo-400 dark:focus:border-violet-500 text-[#1a1625] dark:text-[#e5e5e5] transition-colors"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading ? 'Validando...' : 'Iniciar Sesión'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#6b7280] dark:text-[#888]">
              El motor inteligente para gestionar
            </p>
            <p className="text-sm text-[#6b7280] dark:text-[#888]">
              tu negocio, contenido y métricas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
